import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import Header from "@/components/layout/Header";
import SEOHead from "@/components/seo/SEOHead";
import DashboardNav from "@/components/dashboard/DashboardNav";
import StaticToolBackground from "@/components/layout/StaticToolBackground";
import { useAuth } from "@/hooks/useAuth";
import { useProvider } from "@/hooks/useProvider";
import { supabase } from "@/integrations/supabase/client";
import { TrendingUp, Target, Shield, Award, BarChart3, Clock, CheckCircle, AlertTriangle } from "lucide-react";

const BADGE_LABELS: Record<string, { label: string; icon: typeof Shield }> = {
  verified_business: { label: "Geprüfter Betrieb", icon: Shield },
  insured: { label: "Versichert", icon: CheckCircle },
  premium_partner: { label: "Premium Partner", icon: Award },
  top_rated: { label: "Top bewertet", icon: TrendingUp },
  fast_response: { label: "Schnelle Reaktion", icon: Clock },
  high_acceptance: { label: "Hohe Annahmequote", icon: Target },
};

const TRUST_LEVELS = [
  { level: 0, label: "Basis", desc: "E-Mail verifiziert" },
  { level: 1, label: "Gewerbe", desc: "Gewerbeanmeldung geprüft" },
  { level: 2, label: "Versicherung", desc: "Betriebshaftpflicht bestätigt" },
  { level: 3, label: "Premium", desc: "Bonitäts- & Identitätsprüfung" },
];

export default function ProviderAnalytics() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { profile } = useProvider();

  const { data: stats } = useQuery({
    queryKey: ["provider-kpi", profile?.id],
    queryFn: async () => {
      if (!profile?.id) return null;
      const monthKey = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`;

      const [oppsRes, acceptedRes, wonRes, claimsRes, quotaRes] = await Promise.all([
        supabase.from("opportunities").select("id", { count: "exact" }).eq("provider_id", profile.id),
        supabase.from("opportunities").select("id", { count: "exact" }).eq("provider_id", profile.id).eq("status", "accepted"),
        supabase.from("opportunities").select("id", { count: "exact" }).eq("provider_id", profile.id).eq("outcome", "won"),
        supabase.from("opportunity_claims").select("id, status", { count: "exact" }).eq("provider_id", profile.id),
        supabase.from("provider_monthly_quotas").select("*").eq("provider_id", profile.id).eq("month_key", monthKey).maybeSingle(),
      ]);

      const totalOpps = oppsRes.count || 0;
      const accepted = acceptedRes.count || 0;
      const won = wonRes.count || 0;
      const claims = claimsRes.count || 0;
      const approvedClaims = (claimsRes.data || []).filter((c: any) => c.status === "approved").length;

      return {
        totalOpps,
        accepted,
        won,
        claims,
        approvedClaims,
        acceptRate: totalOpps > 0 ? Math.round((accepted / totalOpps) * 100) : 0,
        conversionRate: accepted > 0 ? Math.round((won / accepted) * 100) : 0,
        disputeRate: accepted > 0 ? Math.round((claims / accepted) * 100) : 0,
        quota: quotaRes.data,
      };
    },
    enabled: !!profile?.id,
  });

  const trustLevel = TRUST_LEVELS.find(tl => tl.level === (profile?.trust_level ?? 0)) || TRUST_LEVELS[0];
  const badges = (profile?.trust_badges as string[]) || [];

  return (
    <div className="min-h-screen bg-background relative">
      <StaticToolBackground />
      <SEOHead title="Analytics & KPIs" noindex canonical="/provider/analytics" />
      <Header />

      <main id="main-content" className="pt-24 pb-12">
        <div className="section-padding border-b border-border">
          <div className="max-w-6xl mx-auto py-6">
            <h1 className="text-headline">Analytics & Performance</h1>
            <p className="text-caption">Ihre KPIs, Trust-Level und Leistungsdaten im Überblick</p>
          </div>
        </div>

        <div className="section-padding">
          <div className="max-w-6xl mx-auto mt-8 space-y-8">
            {/* Trust Level */}
            <div className="border border-border bg-card p-6">
              <div className="flex items-center gap-3 mb-6">
                <Shield className="w-5 h-5 text-accent" />
                <h2 className="text-subheadline">Trust-Profil</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-16 h-16 rounded-full bg-accent/10 border-2 border-accent flex items-center justify-center">
                      <span className="text-2xl font-bold text-accent">{profile?.trust_level ?? 0}</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Level {trustLevel.level}: {trustLevel.label}</p>
                      <p className="text-sm text-muted-foreground">{trustLevel.desc}</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {TRUST_LEVELS.map(tl => (
                      <div key={tl.level} className={`flex items-center gap-2 text-sm ${tl.level <= (profile?.trust_level ?? 0) ? "text-accent" : "text-muted-foreground"}`}>
                        <CheckCircle className={`w-4 h-4 ${tl.level <= (profile?.trust_level ?? 0) ? "text-accent" : "text-muted-foreground/30"}`} />
                        <span>{tl.label}: {tl.desc}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-micro mb-3">TRUST-SCORE</p>
                  <div className="flex items-end gap-2 mb-4">
                    <span className="text-4xl font-bold text-accent">{Math.round(Number(profile?.trust_score) || 0)}</span>
                    <span className="text-muted-foreground text-sm mb-1">/ 100</span>
                  </div>
                  <div className="w-full h-3 bg-secondary rounded-full overflow-hidden">
                    <div className="h-full bg-accent rounded-full transition-all" style={{ width: `${Math.min(100, Number(profile?.trust_score) || 0)}%` }} />
                  </div>
                  {badges.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {badges.map(badge => {
                        const info = BADGE_LABELS[badge];
                        if (!info) return null;
                        return (
                          <span key={badge} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-accent/10 text-accent border border-accent/20">
                            <info.icon className="w-3 h-3" />
                            {info.label}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: "Gesamt-Leads", value: stats?.totalOpps ?? 0, icon: BarChart3 },
                { label: "Angenommen", value: stats?.accepted ?? 0, icon: CheckCircle },
                { label: "Gewonnen", value: stats?.won ?? 0, icon: Award },
                { label: "Reklamationen", value: stats?.claims ?? 0, icon: AlertTriangle },
              ].map(kpi => (
                <div key={kpi.label} className="border border-border bg-card p-5">
                  <kpi.icon className="w-4 h-4 text-accent mb-2" />
                  <p className="text-3xl font-bold text-foreground">{kpi.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{kpi.label}</p>
                </div>
              ))}
            </div>

            {/* Conversion Funnel */}
            <div className="border border-border bg-card p-6">
              <h2 className="text-subheadline mb-6">Conversion Funnel</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-4 border border-border">
                  <p className="text-micro mb-2">LEAD → ANGEBOT</p>
                  <p className="text-4xl font-bold text-accent">{stats?.acceptRate ?? 0}%</p>
                  <p className="text-xs text-muted-foreground mt-1">Annahmequote</p>
                </div>
                <div className="text-center p-4 border border-border">
                  <p className="text-micro mb-2">ANGEBOT → AUFTRAG</p>
                  <p className="text-4xl font-bold text-accent">{stats?.conversionRate ?? 0}%</p>
                  <p className="text-xs text-muted-foreground mt-1">Abschlussquote</p>
                </div>
                <div className="text-center p-4 border border-border">
                  <p className="text-micro mb-2">REKLAMATIONSRATE</p>
                  <p className={`text-4xl font-bold ${(stats?.disputeRate ?? 0) > 10 ? "text-destructive" : "text-accent"}`}>{stats?.disputeRate ?? 0}%</p>
                  <p className="text-xs text-muted-foreground mt-1">{(stats?.disputeRate ?? 0) <= 10 ? "Im grünen Bereich" : "Erhöht – Ranking wird beeinflusst"}</p>
                </div>
              </div>
            </div>

            {/* Monthly Quota */}
            {stats?.quota && (
              <div className="border border-border bg-card p-6">
                <h2 className="text-subheadline mb-4">Monatskontingent</h2>
                <div className="flex items-end gap-2 mb-2">
                  <span className="text-2xl font-bold">{stats.quota.used}</span>
                  <span className="text-muted-foreground text-sm mb-0.5">/ {stats.quota.quota_limit} verwendet</span>
                </div>
                <div className="w-full h-3 bg-secondary rounded-full overflow-hidden">
                  <div className="h-full bg-accent rounded-full transition-all" style={{ width: `${Math.min(100, (stats.quota.used / stats.quota.quota_limit) * 100)}%` }} />
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
